import * as Configuration from '../../app.constants.js';

import { MassMessagingStatusEnum } from './mass-messaging-status.enum.js';

export class MassMessagingService {
  constructor($http, $filter, $mdDialog, UserFactory, AudienceFactory, MetricsFactory, AsyncRequestFactory) {
    this.$http = $http;
    this.$filter = $filter;
    this.$mdDialog = $mdDialog;
    this.UserFactory = UserFactory;
    this.AudienceFactory = AudienceFactory;
    this.MetricsFactory = MetricsFactory;
    this.AsyncRequestFactory = AsyncRequestFactory;
  }

  createMassMessaging(companyId, massMessaging) {
    this._deleteUIFields(massMessaging);
    return this.$http.post(`${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/mass-messaging`, massMessaging);
  }


  updateMassMessaging(companyId, massMessaging) {
    const clonedMassMessaging = angular.copy(massMessaging);
    this._deleteUIFields(clonedMassMessaging);
    return this.$http.put(`${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/mass-messaging/${massMessaging.id}`, clonedMassMessaging);
  }


  sendMassMessaging(companyId, massMessaging) {
    const clonedMassMessaging = angular.copy(massMessaging);
    this._deleteUIFields(clonedMassMessaging);
    const url = `${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/mass-messaging/${massMessaging.id}/send`;
    return this.AsyncRequestFactory.post(companyId, url, clonedMassMessaging);
  }

  pauseMassMessaging(companyId, massMessaging) {
    const clonedMassMessaging = angular.copy(massMessaging);
    this._deleteUIFields(clonedMassMessaging);
    const url = `${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/mass-messaging/${massMessaging.id}/pause`;
    return this.$http.post(url, clonedMassMessaging);
  }

  resumeMassMessaging(companyId, massMessaging) {
    const clonedMassMessaging = angular.copy(massMessaging);
    this._deleteUIFields(clonedMassMessaging);
    const url = `${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/mass-messaging/${massMessaging.id}/resume`;
    return this.$http.post(url, clonedMassMessaging);
  }

  getMassMessagingVolumes(companyId, massMessaging) {
    const url = `${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/mass-messaging/${massMessaging.id}/volumes`;
    return this.$http.get(url);
  }


  buildMassMessagingEntrypointsDropdownItems(scenarios, audienceSites) {
    return scenarios
      .filter(scenario => audienceSites.includes(scenario.site_id))
      .filter(scenario => hasMassMessengingEntrypoint(scenario))
      .map(scenario => ({
        label: scenario.name,
        subitems: massMessengingEntrypoints(scenario).map(box => ({
          id: Number(box.id),
          label: box.entrypoint_description || ''
        }))
      }));
  }


  addUIFields(massMessagingList, sites) {
    massMessagingList.forEach(massMessaging => {
      this._addUIFields(massMessaging, sites);
      if (massMessaging.status === MassMessagingStatusEnum.COMPLETED_STATUS) {
        this.setUIVolumeFieldsFromRawData(massMessaging, massMessaging);
      } else {
        this._setUIVolumeFields(massMessaging, '\u2026', '\u2026', '\u2026', '\u2026');
      }
    });
  }

  _addUIFields(massMessaging, sites) {
    if (massMessaging.creator_admin_user_id) {
      massMessaging.creatorEmail = this.UserFactory.emailForAdminUserId(massMessaging.creator_admin_user_id);
    }
    const range = {
      from: new Date(massMessaging.audience_filters.dateRange.from),
      to: new Date(massMessaging.audience_filters.dateRange.to)
    }
    massMessaging.rangeString = this.AudienceFactory.dateRangeToString(range);
    massMessaging.site = sites.find(site => site.id === massMessaging.audience_filters.accounts[0]); // a mass messaging has one and only one site
    massMessaging.siteSortKey = massMessaging.site.sortKey;
  }

  setUIVolumeFieldsFromRawData(massMessaging, data) {
    const percentage_of_message_sent = Math.round(data.sent / data.total * 100 * 100) / 100;
    const numberOfMessagesSuccessfullySent = `${data.sent} (${percentage_of_message_sent}%)`;
    const remainingNumberOfMessagesToSend = data.total - data.sent - data.notSent - data.errors;

    this._setUIVolumeFields(massMessaging,
      numberOfMessagesSuccessfullySent,
      data.errors,
      remainingNumberOfMessagesToSend,
      data.notSent);
  }

  _setUIVolumeFields(massMessaging, numberOfMessagesSuccessfullySent, numberOfErrors, remainingNumberOfMessagesToSend, numberOfMessagesNotSent) {
    massMessaging.numberOfMessagesSuccessfullySent = numberOfMessagesSuccessfullySent;
    massMessaging.numberOfErrors = numberOfErrors;
    massMessaging.remainingNumberOfMessagesToSend = remainingNumberOfMessagesToSend;
    massMessaging.numberOfMessagesNotSent = numberOfMessagesNotSent;
  }

  _deleteUIFields(massMessaging) {
    delete massMessaging.creatorEmail;
    delete massMessaging.rangeString;
  }

  askForSend(massMessaging) {
    return this.$mdDialog
      .show({
        controller: 'MassMessagingConfirmSendController',
        template: require('./mass-messaging-confirm-send.dialog.html'),
        locals: {
          audienceSize: massMessaging.remainingNumberOfMessagesToSend,
        },
      });
  }

  static MassMessagingFactory($http, $filter, $mdDialog, UserFactory, AudienceFactory, MetricsFactory, AsyncRequestFactory) {
    return new MassMessagingService($http, $filter, $mdDialog, UserFactory, AudienceFactory, MetricsFactory, AsyncRequestFactory);
  }
}

MassMessagingService.MassMessagingFactory.$inject = ['$http', '$filter', '$mdDialog', 'UserFactory', 'AudienceFactory', 'MetricsFactory', 'AsyncRequestFactory'];



function massMessengingEntrypoints(scenario) {
  const allBoxes = Object.values(scenario.graphs).map(e => e.elements ? Object.values(e.elements) : []).flatten();
  return allBoxes.filter(box => box.type === 'massMessagingEntryPointBox');
}

function hasMassMessengingEntrypoint(scenario) {
  return massMessengingEntrypoints(scenario).length > 0;
}
